import { useState, useCallback, useEffect } from 'react';

function storageKey(workspaceId: string | null | undefined, userId: string) {
  const ws = workspaceId ?? 'no_workspace';
  return `pp_my_tasks_archived_v1_${ws}_${userId}`;
}

/**
 * Per-user, per-workspace "archive" for the My Tasks page only.
 * Does not change task status in the database — only hides tasks from the default list.
 */
export function useMyTasksArchive(
  userId: string | null | undefined,
  workspaceId: string | null | undefined
) {
  const [archivedIds, setArchivedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!userId) {
      setArchivedIds(new Set());
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey(workspaceId, userId));
      if (raw) {
        const arr = JSON.parse(raw) as string[];
        setArchivedIds(new Set(Array.isArray(arr) ? arr : []));
      } else {
        setArchivedIds(new Set());
      }
    } catch {
      setArchivedIds(new Set());
    }
  }, [userId, workspaceId]);

  const persist = useCallback(
    (next: Set<string>) => {
      if (!userId) return;
      try {
        if (next.size === 0) {
          localStorage.removeItem(storageKey(workspaceId, userId));
        } else {
          localStorage.setItem(storageKey(workspaceId, userId), JSON.stringify([...next]));
        }
      } catch {
        // quota / private mode
      }
    },
    [userId, workspaceId]
  );

  const archiveTaskIds = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      setArchivedIds(prev => {
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const restoreTaskIds = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      setArchivedIds(prev => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        persist(next);
        return next;
      });
    },
    [persist]
  );

  return { archivedIds, archiveTaskIds, restoreTaskIds };
}
